import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useTranslation } from "react-i18next";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { History } from "lucide-react";
import { StoricoRicerca } from "@/components/StoricoRicerca";

const fmt = (n) =>
  (n ?? 0).toLocaleString("it-IT", { minimumFractionDigits: 0, maximumFractionDigits: 1 });

export const StoricoAnnuale = () => {
  const { t } = useTranslation("app");
  const [data, setData] = useState(null);
  const [anno, setAnno] = useState(null);

  useEffect(() => {
    api.get("/statistiche/storico-annuale")
      .then(({ data }) => {
        setData(data);
        const anni = data?.anni || [];
        if (anni.length) setAnno(anni[anni.length - 1].anno);
      })
      .catch(() => setData({ anni: [] }));
  }, []);

  if (!data) return null;
  const anni = data.anni || [];
  if (!anni.length) {
    return (
      <Card className="p-6 text-center text-slate-500" data-testid="storico-empty">
        {t("stats.storicoEmpty")}
      </Card>
    );
  }

  const bars = [
    ...anni.map((a) => ({ anno: a.anno, ore: a.ore_tot, current: false })),
    ...(data.anno_corrente?.ore_tot
      ? [{ anno: data.anno_corrente.anno, ore: data.anno_corrente.ore_tot, current: true }]
      : []),
  ];
  const max = Math.max(...bars.map((b) => b.ore), 1);
  const sel = anni.find((a) => a.anno === anno) || anni[anni.length - 1];

  return (
    <div className="space-y-4" data-testid="storico-annuale-section">
      <StoricoRicerca />
      <Card className="p-4 md:p-6 space-y-5">
        <div>
          <div className="font-heading font-bold tracking-tight flex items-center gap-2">
            <History className="h-5 w-5 text-[#0044FF]" /> {t("stats.storicoTitle")}
          </div>
          <p className="text-sm text-slate-600 mt-1">{t("stats.storicoSub")}</p>
        </div>
        <div className="flex items-end gap-2 md:gap-4 h-48" data-testid="storico-bar-chart">
          {bars.map((b) => (
            <div key={b.anno} className="flex-1 flex flex-col items-center justify-end h-full gap-1">
              <div className="font-mono text-xs md:text-sm font-bold text-slate-700">
                {fmt(b.ore)}
              </div>
              <div
                className={`w-full rounded-t-md transition-all ${
                  b.current ? "bg-emerald-500" : "bg-[#0044FF]"
                }`}
                style={{ height: `${Math.max((b.ore / max) * 100, 3)}%` }}
                title={`${b.anno}: ${fmt(b.ore)} h`}
              />
              <div className="text-xs md:text-sm font-bold text-slate-800">
                {b.anno}
                {b.current && (
                  <span className="block text-[10px] font-normal text-emerald-600">
                    {t("stats.storicoInCorso")}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </Card>

      <Card className="p-4 md:p-6 space-y-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-bold text-slate-700 mr-1">
            {t("stats.storicoDettaglio")}:
          </span>
          {anni.map((a) => (
            <Button
              key={a.anno}
              size="sm"
              variant={a.anno === sel.anno ? "default" : "outline"}
              onClick={() => setAnno(a.anno)}
              data-testid={`storico-year-${a.anno}`}
            >
              {a.anno}
            </Button>
          ))}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-center">
          {[
            [t("stats.storicoTotale"), sel.ore_tot, true],
            [t("stats.storicoOrd"), sel.ore_o, false],
            [t("stats.storicoStra"), sel.ore_s, false],
            [t("stats.storicoViaggio"), sel.ore_v, false],
            [t("stats.storicoOfficina"), sel.ore_off, false],
          ].map(([label, val, hl]) => (
            <div
              key={label}
              className={`rounded-md px-3 py-2 ${
                hl ? "bg-[#0044FF] text-white" : "bg-white border border-slate-200"
              }`}
            >
              <div className="text-[10px] uppercase tracking-wider opacity-70">{label}</div>
              <div className="font-mono font-bold text-lg">{fmt(val)}</div>
            </div>
          ))}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="storico-top-table">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="px-2 py-1.5">#</th>
                <th className="px-2 py-1.5">{t("stats.storicoCommessa")}</th>
                <th className="px-2 py-1.5 text-right">{t("stats.storicoCantiere")}</th>
                <th className="px-2 py-1.5 text-right">{t("stats.storicoOfficina")}</th>
                <th className="px-2 py-1.5 text-right">{t("stats.storicoTotale")}</th>
              </tr>
            </thead>
            <tbody>
              {(sel.top_commesse || []).map((c, i) => (
                <tr key={c.codice} className="border-b border-slate-100">
                  <td className="px-2 py-1.5 text-slate-400">{i + 1}</td>
                  <td className="px-2 py-1.5 font-mono font-bold">{c.codice}</td>
                  <td className="px-2 py-1.5 text-right font-mono">{fmt(c.ore_cantiere)}</td>
                  <td className="px-2 py-1.5 text-right font-mono">{fmt(c.ore_off)}</td>
                  <td className="px-2 py-1.5 text-right font-mono font-bold">{fmt(c.ore_tot)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-xs text-slate-500 mt-2">
            {t("stats.storicoNota", { n: sel.n_commesse })}
          </p>
        </div>
      </Card>
    </div>
  );
};
